import { zeroErrorMessage } from "@/lib/zero-error";
import { useQuery as useZeroQuery, useZero } from "@rocicorp/zero/react";
import { appMutators, appQueries } from "@production-hub/shared";
import { useState } from "react";
import { toast } from "sonner";
import { toFriendlyMutationError } from "@/lib/error-utils";
import {
  TemplateTranslations,
  QuoteLineItemTemplate,
} from "./useQuoteLineItemTemplates";
import { generateUUID } from "@/lib/utils";

export interface QuoteGroupTemplateItem {
  id: string;
  group_template_id: string;
  line_item_template_id: string | null;
  is_title: boolean;
  name: TemplateTranslations | null;
  default_amount: number;
  sort_order: number;
  created_at: string;
  updated_at: string;
  template: QuoteLineItemTemplate | null;
}

export interface QuoteGroupTemplate {
  id: string;
  name: TemplateTranslations;
  default_is_optional: boolean;
  default_apply_project_discount: boolean;
  category: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
  items: QuoteGroupTemplateItem[];
}

type ZeroMutationResult =
  | { type: "error"; error: { message: string } }
  | { type: string; error?: never };
type ZeroMutateClient = {
  mutate: (mutation: unknown) => {
    client: Promise<void>;
    server: Promise<ZeroMutationResult>;
  };
};
type MutationOptions<T> = { onSuccess?: (data: T) => void };

type ZeroQuoteGroupTemplateRow = {
  id: string;
  name: TemplateTranslations;
  defaultIsOptional: boolean;
  defaultApplyProjectDiscount: boolean;
  category?: string | null;
  sortOrder: number;
  createdAt?: number | string | null;
  updatedAt?: number | string | null;
  items?: Array<{
    id: string;
    groupTemplateID: string;
    lineItemTemplateID?: string | null;
    isTitle: boolean;
    name?: TemplateTranslations | null;
    defaultAmount: number;
    sortOrder: number;
    createdAt?: number | string | null;
    updatedAt?: number | string | null;
    template?: {
      id: string;
      name: TemplateTranslations;
      code?: string | null;
      defaultAmount: number;
      defaultUnitPrice: number;
      lineItemType: "generic" | "equipment" | "crew";
      category?: string | null;
      sortOrder: number;
      createdAt?: number | string | null;
      updatedAt?: number | string | null;
    } | null;
  }> | null;
};

function toLegacyTimestamp(value: number | string | null | undefined) {
  if (typeof value === "number") return new Date(value).toISOString();
  return value ?? "";
}

function toLegacyLineItemTemplate(
  template: NonNullable<
    NonNullable<ZeroQuoteGroupTemplateRow["items"]>[number]["template"]
  >,
): QuoteLineItemTemplate {
  return {
    id: template.id,
    name: template.name,
    code: template.code ?? null,
    default_amount: template.defaultAmount,
    default_unit_price: template.defaultUnitPrice,
    line_item_type: template.lineItemType,
    category: template.category ?? null,
    sort_order: template.sortOrder,
    created_at: toLegacyTimestamp(template.createdAt),
    updated_at: toLegacyTimestamp(template.updatedAt),
  };
}

function toLegacyGroupTemplate(
  row: ZeroQuoteGroupTemplateRow,
): QuoteGroupTemplate {
  return {
    id: row.id,
    name: row.name,
    default_is_optional: row.defaultIsOptional,
    default_apply_project_discount: row.defaultApplyProjectDiscount,
    category: row.category ?? null,
    sort_order: row.sortOrder,
    created_at: toLegacyTimestamp(row.createdAt),
    updated_at: toLegacyTimestamp(row.updatedAt),
    items: (row.items ?? [])
      .filter((item) => item.isTitle || !!item.template)
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((item) => ({
        id: item.id,
        group_template_id: item.groupTemplateID,
        line_item_template_id: item.lineItemTemplateID ?? null,
        is_title: item.isTitle,
        name: item.name ?? null,
        default_amount: item.defaultAmount,
        sort_order: item.sortOrder,
        created_at: toLegacyTimestamp(item.createdAt),
        updated_at: toLegacyTimestamp(item.updatedAt),
        template: item.template
          ? toLegacyLineItemTemplate(item.template)
          : null,
      })),
  };
}

async function reportZeroMutationFailure(
  serverResult: Promise<ZeroMutationResult>,
  fallback: string,
) {
  const result = await serverResult;
  if (result.type === "error") {
    throw new Error(result.error?.message || fallback);
  }
}

function useZeroMutation<TInput, TResult>({
  run,
  success,
  failure,
}: {
  run: (input: TInput) => Promise<TResult>;
  success: string;
  failure: string;
}) {
  const [isPending, setIsPending] = useState(false);

  const wrappedRun = async (input: TInput) => {
    setIsPending(true);
    try {
      return await run(input);
    } catch (error) {
      throw toFriendlyMutationError(error);
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate: (input: TInput, options?: MutationOptions<TResult>) => {
      void wrappedRun(input)
        .then((data) => {
          toast.success(success);
          options?.onSuccess?.(data);
        })
        .catch((error) => toast.error(`${failure}: ${error.message}`));
    },
    mutateAsync: wrappedRun,
    isPending,
  };
}

export function useQuoteGroupTemplates() {
  const [data, details] = useZeroQuery(appQueries.quoteGroupTemplates.list());
  const templates = ((data ?? []) as ZeroQuoteGroupTemplateRow[]).map(
    toLegacyGroupTemplate,
  );

  return {
    data: templates,
    isLoading: details.type !== "complete" && templates.length === 0,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}

type GroupTemplateInput = {
  name: TemplateTranslations;
  default_is_optional?: boolean;
  default_apply_project_discount?: boolean;
  category?: string | null;
  sort_order?: number;
  items?: Array<{
    line_item_template_id?: string | null;
    default_amount?: number;
    sort_order?: number;
    is_title?: boolean;
    name?: TemplateTranslations | null;
  }>;
};

function toZeroItems(items: GroupTemplateInput["items"]) {
  return (items ?? []).map((item) => ({
    lineItemTemplateID: item.line_item_template_id ?? null,
    defaultAmount: item.default_amount ?? (item.is_title ? 0 : 1),
    sortOrder: item.sort_order,
    isTitle: item.is_title ?? false,
    name: item.name ?? null,
  }));
}

export function useCreateQuoteGroupTemplate() {
  const zero = useZero() as ZeroMutateClient;

  return useZeroMutation<GroupTemplateInput, QuoteGroupTemplate>({
    success: "Group template created successfully",
    failure: "Failed to create group template",
    run: async (template) => {
      const id = generateUUID();
      const mutation = zero.mutate(
        appMutators.quoteGroupTemplates.create({
          id,
          name: template.name,
          defaultIsOptional: template.default_is_optional ?? false,
          defaultApplyProjectDiscount:
            template.default_apply_project_discount ?? true,
          category: template.category ?? null,
          sortOrder: template.sort_order ?? 0,
          items: toZeroItems(template.items),
        }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Group template create rejected",
      );
      return {
        id,
        name: template.name,
        default_is_optional: template.default_is_optional ?? false,
        default_apply_project_discount:
          template.default_apply_project_discount ?? true,
        category: template.category ?? null,
        sort_order: template.sort_order ?? 0,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        items: [],
      };
    },
  });
}

export function useUpdateQuoteGroupTemplate() {
  const zero = useZero() as ZeroMutateClient;

  return useZeroMutation<
    {
      id: string;
      updates: Partial<{
        name: TemplateTranslations;
        default_is_optional: boolean;
        default_apply_project_discount: boolean;
        category: string | null;
        sort_order: number;
      }>;
      items?: GroupTemplateInput["items"];
    },
    { id: string }
  >({
    success: "Group template updated successfully",
    failure: "Failed to update group template",
    run: async ({ id, updates, items }) => {
      const mutation = zero.mutate(
        appMutators.quoteGroupTemplates.update({
          id,
          name: updates.name,
          defaultIsOptional: updates.default_is_optional,
          defaultApplyProjectDiscount: updates.default_apply_project_discount,
          category: updates.category,
          sortOrder: updates.sort_order,
          items: items === undefined ? undefined : toZeroItems(items),
        }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Group template update rejected",
      );
      return { id };
    },
  });
}

export function useRenameGroupTemplateCategory() {
  const zero = useZero() as ZeroMutateClient;

  return useZeroMutation<
    { oldCategory: string; newCategory: string },
    { oldCategory: string; newCategory: string }
  >({
    success: "Category renamed successfully",
    failure: "Failed to rename category",
    run: async (input) => {
      const mutation = zero.mutate(
        appMutators.quoteGroupTemplates.renameCategory(input),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Group template category rename rejected",
      );
      return input;
    },
  });
}

export function useDeleteQuoteGroupTemplate() {
  const zero = useZero() as ZeroMutateClient;

  return useZeroMutation<string, string>({
    success: "Group template deleted successfully",
    failure: "Failed to delete group template",
    run: async (id) => {
      const mutation = zero.mutate(
        appMutators.quoteGroupTemplates.delete({ id }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Group template delete rejected",
      );
      return id;
    },
  });
}
